import { Link, usePage } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { useEffect, useState } from "react"

import { HelpProse } from "@/components/help/help-prose"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { help_article_path, help_panel_path } from "@/routes"
import type { SharedData } from "@/types"

interface Article {
  slug: string
  title: string
  summary: string
  section: string
  body: string
}

/**
 * One article, over the page rather than instead of it.
 *
 * The body is fetched when the sheet opens instead of riding on every request:
 * 23 articles of prose shipped on every page load to power a panel most people
 * never open is a payload nobody reads. Titles and summaries are already in
 * shared props for ⌘K, so the list is instant and only the reading costs a
 * round trip.
 *
 * Built on the Radix-backed Sheet, which brings the focus trap, the ESC handler
 * and the return-focus behaviour with it — none of which is worth reimplementing
 * and all of which is worth having.
 */
export function HelpSheet({
  slug,
  onClose,
  onCloseAutoFocus,
}: {
  slug: string | null
  onClose: () => void
  onCloseAutoFocus?: (event: Event) => void
}) {
  // Absent on the portal. `PersistentLayout` mounts this on every page that
  // is not under `pages/marketing/`, which includes a desk's storefront —
  // where there is no account scope, no session and no `current_account`.
  // Dereferencing it in the effect deps below crashed the whole page (INCO-388).
  const { current_account: account } = usePage<Partial<SharedData>>().props
  // Read out here so the effect below depends on the uid rather than on the
  // object identity of a shared prop, which changes on every Inertia visit.
  const accountUid = account?.uid
  const [article, setArticle] = useState<Article | null>(null)
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    if (!slug || !accountUid) return

    let cancelled = false
    setArticle(null)
    setMissing(false)

    fetch(help_panel_path(accountUid, slug), { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return
        if (data.missing) setMissing(true)
        else setArticle(data as Article)
      })
      // A network failure and a missing article look the same to the reader,
      // and the honest thing to say in both cases is that we could not show it
      // — never a blank panel, which reads as broken.
      .catch(() => !cancelled && setMissing(true))

    return () => {
      cancelled = true
    }
  }, [slug, accountUid])

  // Nothing to open a help sheet against.
  if (!account) return null

  return (
    <Sheet open={slug !== null} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        onCloseAutoFocus={onCloseAutoFocus}
        // The width has to carry the same `data-[side=right]` variant as the
        // component's own `sm:max-w-sm`, or it loses on specificity. It read
        // `sm:max-w-xl` for months and the sheet was 384px the whole time.
        className="w-full gap-0 overflow-y-auto data-[side=right]:sm:max-w-lg"
      >
        {missing ? (
          <>
            <SheetHeader className="p-6 pb-4">
              <SheetTitle className="text-xl font-medium">
                We have not written that one yet
              </SheetTitle>
              <SheetDescription>
                There is no article for “{slug}”. It may have been renamed, or it may be a
                gap — either is worth telling us about with the feedback button in the
                sidebar.
              </SheetDescription>
            </SheetHeader>
            <div className="px-6 pb-6">
              <Link
                href={`/${account.uid}/help`}
                className="text-sm underline underline-offset-[3px]"
              >
                Browse the help centre
              </Link>
            </div>
          </>
        ) : article === null ? (
          <div className="space-y-3 p-6" aria-busy="true" aria-label="Loading article">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        ) : (
          <>
            <SheetHeader className="gap-1 p-6 pb-4">
              <p className="text-muted-foreground text-xs font-semibold">
                {article.section}
              </p>
              <SheetTitle className="text-xl leading-[1.15] font-medium tracking-[-0.01em]">
                {article.title}
              </SheetTitle>
              {/* The article's own body size, so the summary reads as its
                  first paragraph rather than as a caption on the title. */}
              <SheetDescription className="text-base leading-[1.65]">
                {article.summary}
              </SheetDescription>
            </SheetHeader>

            <div className="px-6 pb-10">
              <HelpProse body={article.body} />

              {/*
                The way out to the full page, for somebody who wants to read
                around the subject rather than answer one question. Navigating
                is their choice here; it was not forced on them to read this.
              */}
              <Link
                href={help_article_path(account.uid, article.slug)}
                className="text-muted-foreground hover:text-foreground mt-8 inline-flex items-center gap-1.5 text-sm transition-colors"
              >
                Open as a page
                <ExternalLink className="size-3.5" aria-hidden />
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
